import { apiPost } from '@/lib/apiFetch';

// Bucket name and public host are not secrets. The R2 access key and secret
// live only on the server - see api/r2-sign.ts.
const R2_BUCKET     = import.meta.env.VITE_R2_BUCKET as string;
const R2_PUBLIC_URL = (import.meta.env.VITE_R2_PUBLIC_URL as string)?.replace(/\/$/, '');

/**
 * Build the public URL for an R2 object key. Encoded per path segment, since a
 * key may legally contain spaces that have to be escaped in a URL.
 */
export function r2Url(key: string): string {
  return `${R2_PUBLIC_URL}/${key.split('/').map(encodeURIComponent).join('/')}`;
}

/**
 * Ask the server for a short-lived presigned PUT URL. Use this directly only
 * when you need control over the request (e.g. XHR for upload progress);
 * otherwise prefer r2Upload.
 */
export async function r2PresignPut(
  key: string,
  contentType: string,
): Promise<{ url: string; publicUrl: string }> {
  return apiPost<{ url: string; publicUrl: string }>('/api/r2-sign', { op: 'put', key, contentType });
}

/** Upload a file/blob to R2 via a short-lived presigned URL, returning its public URL. */
export async function r2Upload(
  key: string,
  body: Blob | File | Uint8Array | ArrayBuffer,
  contentType: string,
): Promise<string> {
  const { url, publicUrl } = await r2PresignPut(key, contentType);

  // Content-Type must match what was signed, or R2 rejects the signature.
  const res = await fetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body: body as BodyInit,
  });
  if (!res.ok) throw new Error(`Upload failed (${res.status})`);

  return publicUrl || r2Url(key);
}

/** Delete an object from R2. */
export async function r2Delete(key: string): Promise<void> {
  await apiPost('/api/r2-sign', { op: 'delete', key });
}

export { R2_BUCKET, R2_PUBLIC_URL };
